<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  BriefcaseBusiness,
  BookOpen,
  CheckSquare,
  Clock3,
  CornerDownLeft,
  FolderKanban,
  History,
  LayoutDashboard,
  Loader2,
  Search,
  Sparkles,
  UserRound,
} from 'lucide-vue-next'
import { clientOwnershipLabel } from '~/lib/clientOwnership'
import type { AppNavGroup, AppNavItem } from '~/composables/useAppNavigation'
import type { Project, Task } from '~/types/models'

type QuickActionItem = {
  id: string
  label: string
  icon: any
  action: () => void | Promise<void>
}

type SearchEntryGroup =
  | 'suggested'
  | 'quick-actions'
  | 'pages'
  | 'projects'
  | 'tasks'
  | 'clients'
  | 'documents'
  | 'crm'
  | 'account'
  | 'recent'

type SearchEntry = {
  id: string
  title: string
  description: string
  icon: any
  group: SearchEntryGroup
  keywords: string[]
  url?: string
  action?: () => void | Promise<void>
  isCurrent?: boolean
}

type SearchSection = {
  label: string
  icon: any
  entries: SearchEntry[]
}

const props = withDefaults(defineProps<{
  workspaceName: string
  navGroups: AppNavGroup[]
  accountItems?: AppNavItem[]
  quickActions?: QuickActionItem[]
  isItemActive: (groupLabel: string, url: string) => boolean
}>(), {
  accountItems: () => [],
  quickActions: () => [],
})

const route = useRoute()
const { tenantPath } = useTenantPath()
const projectStore = useProjectStore()
const taskStore = useTaskStore()
const clientStore = useClientStore()
const documentsStore = useWorkspaceDocumentsStore()

const rootRef = ref<HTMLElement | null>(null)
const inputRef = ref<HTMLInputElement | null>(null)
const mobileInputRef = ref<HTMLInputElement | null>(null)
const open = ref(false)
const searchQuery = ref('')
const recentUrls = ref<string[]>([])
const shortcutLabel = ref('Ctrl K')
const highlightedIndex = ref(0)
const workspaceRecordsLoading = ref(false)
const workspaceRecordsLoaded = ref(false)

const recentStorageKey = computed(() => {
  const tenantSlug = typeof route.params.tenantSlug === 'string' && route.params.tenantSlug
    ? route.params.tenantSlug
    : 'workspace'

  return `blossum:workspace-command-recent:${tenantSlug}`
})

function buildPath(path: string, query?: Record<string, string | undefined>) {
  const params = new URLSearchParams()

  for (const [key, value] of Object.entries(query || {})) {
    if (!value) continue
    params.set(key, value)
  }

  const queryString = params.toString()
  return queryString ? `${path}?${queryString}` : path
}

function normalizeSearchText(value: unknown): string {
  return String(value || '').trim().toLowerCase()
}

function toDisplayDate(value: unknown): string {
  if (!value) return ''

  const parsed = new Date(String(value))
  if (Number.isNaN(parsed.getTime())) return ''

  return parsed.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  })
}

function taskDestination(task: Task, projectById: Map<string, Project>) {
  const project = task.projectId ? projectById.get(task.projectId) : null
  if (project?.slug) {
    return buildPath(tenantPath(`/clients/projects/${project.slug}`), {
      tab: 'tasks',
      taskId: task.id,
    })
  }

  if (task.projectId) {
    return buildPath(tenantPath(`/projects/${task.projectId}`), {
      tab: 'tasks',
      taskId: task.id,
    })
  }

  return buildPath(tenantPath('/tasks'), {
    taskId: task.id,
  })
}

function projectWorkspaceDestination(project: Project, tab?: string) {
  const basePath = project.slug
    ? tenantPath(`/clients/projects/${project.slug}`)
    : tenantPath(`/projects/${project.id}`)

  return buildPath(basePath, tab ? { tab } : undefined)
}

function matchScore(entry: SearchEntry, query: string) {
  const normalizedTitle = normalizeSearchText(entry.title)
  const normalizedDescription = normalizeSearchText(entry.description)
  const normalizedKeywords = entry.keywords.map(normalizeSearchText)

  if (normalizedTitle === query) return 400
  if (normalizedTitle.startsWith(query)) return 240
  if (normalizedKeywords.some((keyword) => keyword.startsWith(query))) return 220
  if (normalizedTitle.includes(query)) return 200
  if (normalizedDescription.includes(query)) return 150
  if (normalizedKeywords.some((keyword) => keyword.includes(query))) return 120
  return 0
}

const navEntries = computed<SearchEntry[]>(() => (
  props.navGroups.flatMap((group) => (
    group.items
      .filter((item) => item.url)
      .map((item) => ({
        id: `nav:${group.id}:${item.title}`,
        title: item.title,
        description: `${group.label || 'Workspace'} page`,
        icon: item.icon,
        group: 'pages' as const,
        keywords: [
          group.label || 'Workspace',
          item.badge || '',
          'page',
          item.title === 'Dashboard' ? 'workspace dashboard my hub home overview' : '',
          item.title === 'Inbox' ? 'messages inbox communication threads email whatsapp' : '',
          item.title === 'Tasks' ? 'workspace task list all tasks board' : '',
          item.title === 'Projects' ? 'project list delivery workspaces' : '',
          item.title === 'Clients' ? 'crm clients opportunities leads enquiries' : '',
          item.title === 'Onboarding' ? 'client onboarding active runs journeys processes' : '',
          item.title === 'Pipelines' ? 'onboarding pipeline templates stages requirements processes' : '',
        ],
        url: item.url,
        isCurrent: item.url ? props.isItemActive(group.label || 'Workspace', item.url) : false,
      }))
  ))
))

const quickActionEntries = computed<SearchEntry[]>(() => (
  props.quickActions.map((item) => ({
    id: `quick:${item.id}`,
    title: item.label,
    description: 'Quick action',
    icon: item.icon,
    group: 'quick-actions' as const,
    keywords: ['create', 'new', 'quick action', item.label],
    action: item.action,
  }))
))

const accountEntries = computed<SearchEntry[]>(() => (
  props.accountItems.map((item) => ({
    id: `account:${item.title}`,
    title: item.title,
    description: 'Account and settings',
    icon: item.icon,
    group: 'account' as const,
    keywords: ['account', 'settings', item.title, item.badge || ''],
    url: item.url,
    action: item.action,
  }))
))

const projectById = computed(() => new Map(projectStore.projects.map((project) => [project.id, project] as const)))
const clientNameById = computed(() => Object.fromEntries(
  clientStore.clients.map((client) => [client.id, client.companyName || client.name || client.id]),
))

const projectEntries = computed<SearchEntry[]>(() => (
  projectStore.projects.flatMap((project) => {
    const clientName = clientOwnershipLabel(project, {
      clientNameById: clientNameById.value,
    }) || 'Project workspace'

    return [
      {
        id: `project:${project.id}:workspace`,
        title: project.name,
        description: `Project workspace · ${clientName}`,
        icon: FolderKanban,
        group: 'projects' as const,
        keywords: [
          project.name,
          project.description || '',
          clientName,
          project.status || '',
          'project workspace delivery overview',
        ],
        url: projectWorkspaceDestination(project),
      },
      {
        id: `project:${project.id}:tasks`,
        title: `${project.name} tasks`,
        description: `Project task list · ${clientName}`,
        icon: CheckSquare,
        group: 'projects' as const,
        keywords: [
          project.name,
          clientName,
          'task list',
          'project tasks',
          'project checklist',
          'delivery tasks',
        ],
        url: projectWorkspaceDestination(project, 'tasks'),
      },
    ]
  })
))

const taskEntries = computed<SearchEntry[]>(() => (
  taskStore.tasks.map((task) => {
    const project = task.projectId ? projectById.value.get(task.projectId) : null
    const clientName = clientOwnershipLabel(task, {
      clientNameById: clientNameById.value,
    })
    const projectName = project?.name || 'Workspace task'
    const statusLabel = task.status || 'No status'
    const dueLabel = toDisplayDate(task.dueDate)
    const descriptionParts = [
      project?.name ? `Task in ${project.name}` : 'Workspace task',
      statusLabel,
      dueLabel ? `due ${dueLabel}` : '',
    ].filter(Boolean)

    return {
      id: `task:${task.id}`,
      title: task.title || 'Untitled task',
      description: descriptionParts.join(' · '),
      icon: CheckSquare,
      group: 'tasks' as const,
      keywords: [
        task.title,
        task.description || '',
        task.status || '',
        task.priority || '',
        task.assignee || '',
        projectName,
        clientName,
        'task checklist todo action item',
      ],
      url: taskDestination(task, projectById.value),
    }
  })
))

const clientEntries = computed<SearchEntry[]>(() => (
  clientStore.clients.map((client) => {
    const lifecycleStatus = String(client.lifecycle?.status || '').trim() || 'client'
    const url = client.slug
      ? tenantPath(`/clients/${client.slug}`)
      : tenantPath('/clients')

    return {
      id: `client:${client.id}`,
      title: client.companyName || client.name,
      description: `Client workspace · ${lifecycleStatus.replace(/_/g, ' ')}`,
      icon: UserRound,
      group: 'clients' as const,
      keywords: [
        client.name,
        client.companyName || '',
        client.email || '',
        client.contactName || '',
        lifecycleStatus,
        'client customer account',
      ],
      url,
    }
  })
))

const crmEntries = computed<SearchEntry[]>(() => (
  clientStore.clients.map((client) => ({
    id: `crm:${client.id}`,
    title: client.companyName || client.name || 'Client',
    description: `CRM workspace · ${String(client.lifecycle?.status || 'client').replace(/_/g, ' ')}`,
    icon: BriefcaseBusiness,
    group: 'crm' as const,
    keywords: [
      client.name,
      client.companyName || '',
      client.email || '',
      client.contactName || '',
      String(client.lifecycle?.status || ''),
      String(client.lifecycle?.pipelineStage || ''),
      String(client.lifecycle?.commercialStage || ''),
      'crm lead prospect opportunity client',
    ],
    url: client.slug ? tenantPath(`/clients/${client.slug}`) : tenantPath('/clients'),
  }))
))

const documentEntries = computed<SearchEntry[]>(() => (
  documentsStore.documents.map((document) => ({
    id: `document:${document.id}`,
    title: document.title || 'Untitled document',
    description: `Documentation · ${document.projectName || document.clientName || 'Workspace'}`,
    icon: BookOpen,
    group: 'documents' as const,
    keywords: [
      document.title,
      document.content.replace(/<[^>]+>/g, ' '),
      document.projectName || '',
      document.clientName || '',
      ...(document.tags || []),
      'documentation knowledge process guide reference',
    ],
    url: tenantPath({ path: '/documentation', query: { document: document.id } }),
  }))
))

const activePageEntry = computed(() => (
  navEntries.value.find((entry) => entry.isCurrent) || null
))

const recentEntries = computed<SearchEntry[]>(() => {
  const seen = new Set<string>()
  const entries: SearchEntry[] = []

  for (const url of recentUrls.value) {
    const candidates = [
      ...navEntries.value,
      ...projectEntries.value,
      ...taskEntries.value,
      ...clientEntries.value,
      ...crmEntries.value,
      ...documentEntries.value,
      ...accountEntries.value,
    ]
    const entry = candidates.find((candidate) => candidate.url === url)
    if (!entry || !entry.url || seen.has(entry.url)) continue
    seen.add(entry.url)
    entries.push({ ...entry, group: 'recent' })
  }

  return entries
})

const suggestedEntries = computed<SearchEntry[]>(() => {
  const entries: SearchEntry[] = []
  const pushedIds = new Set<string>()

  const pushEntry = (entry: SearchEntry | null | undefined) => {
    if (!entry || pushedIds.has(entry.id)) return
    pushedIds.add(entry.id)
    entries.push({ ...entry, group: 'suggested' })
  }

  pushEntry(activePageEntry.value)
  pushEntry(navEntries.value.find((entry) => entry.title === 'Tasks'))
  pushEntry(navEntries.value.find((entry) => entry.title === 'Projects'))
  pushEntry(navEntries.value.find((entry) => entry.title === 'My Hub'))
  pushEntry(navEntries.value.find((entry) => entry.title === 'Calendar'))
  pushEntry(navEntries.value.find((entry) => entry.title === 'Messages'))

  return entries.slice(0, 6)
})

function loadRecentUrls() {
  if (!import.meta.client) {
    recentUrls.value = []
    return
  }

  try {
    const stored = window.localStorage.getItem(recentStorageKey.value)
    const parsed = stored ? JSON.parse(stored) : []
    recentUrls.value = Array.isArray(parsed)
      ? parsed.map((value) => String(value)).filter(Boolean)
      : []
  } catch {
    recentUrls.value = []
  }
}

function rememberRecentUrl(url: string) {
  if (!import.meta.client || !url) return

  const next = [url, ...recentUrls.value.filter((entry) => entry !== url)].slice(0, 10)
  recentUrls.value = next
  window.localStorage.setItem(recentStorageKey.value, JSON.stringify(next))
}

async function ensureWorkspaceRecordsLoaded() {
  if (workspaceRecordsLoading.value || workspaceRecordsLoaded.value) return

  workspaceRecordsLoading.value = true

  try {
    await Promise.all([
      projectStore.projects.length === 0 ? projectStore.fetchProjects() : Promise.resolve(),
      taskStore.tasks.length === 0 ? taskStore.fetchTasks() : Promise.resolve(),
      clientStore.clients.length === 0 ? clientStore.fetchClients() : Promise.resolve(),
      documentsStore.documents.length === 0 ? documentsStore.fetchAll() : Promise.resolve(),
    ])
  } finally {
    workspaceRecordsLoaded.value = true
    workspaceRecordsLoading.value = false
  }
}

function matchesQuery(entry: SearchEntry, query: string) {
  const haystack = [
    entry.title,
    entry.description,
    ...entry.keywords,
  ]
    .join(' ')
    .toLowerCase()

  return haystack.includes(query)
}

function sortedMatches(entries: SearchEntry[], query: string) {
  return [...entries]
    .filter((entry) => matchesQuery(entry, query))
    .sort((a, b) => {
      const scoreDifference = matchScore(b, query) - matchScore(a, query)
      if (scoreDifference !== 0) return scoreDifference
      return a.title.localeCompare(b.title)
    })
}

function focusSearchInput() {
  if (!import.meta.client) return

  if (window.innerWidth >= 768) {
    inputRef.value?.focus()
    return
  }

  mobileInputRef.value?.focus()
}

const filteredSections = computed<SearchSection[]>(() => {
  const query = searchQuery.value.trim().toLowerCase()

  if (!query) {
    return [
      { label: 'Suggested', icon: Sparkles, entries: suggestedEntries.value },
      { label: 'Recent', icon: History, entries: recentEntries.value.slice(0, 5) },
      { label: 'Quick Actions', icon: Sparkles, entries: quickActionEntries.value.slice(0, 5) },
      { label: 'Pages', icon: LayoutDashboard, entries: navEntries.value.slice(0, 8) },
    ].filter((section) => section.entries.length > 0)
  }

  return [
    { label: 'Projects', icon: FolderKanban, entries: sortedMatches(projectEntries.value, query).slice(0, 6) },
    { label: 'Tasks', icon: CheckSquare, entries: sortedMatches(taskEntries.value, query).slice(0, 7) },
    { label: 'Clients', icon: UserRound, entries: sortedMatches(clientEntries.value, query).slice(0, 5) },
    { label: 'CRM', icon: BriefcaseBusiness, entries: sortedMatches(crmEntries.value, query).slice(0, 5) },
    { label: 'Documentation', icon: BookOpen, entries: sortedMatches(documentEntries.value, query).slice(0, 6) },
    { label: 'Pages', icon: LayoutDashboard, entries: sortedMatches(navEntries.value, query).slice(0, 6) },
    { label: 'Quick Actions', icon: Sparkles, entries: sortedMatches(quickActionEntries.value, query).slice(0, 4) },
    { label: 'Account', icon: Clock3, entries: sortedMatches(accountEntries.value, query).slice(0, 4) },
  ].filter((section) => section.entries.length > 0)
})

const flattenedEntries = computed(() => filteredSections.value.flatMap((section) => section.entries))
const activeEntry = computed(() => flattenedEntries.value[highlightedIndex.value] || null)

async function openSearch() {
  if (open.value) return

  open.value = true
  loadRecentUrls()
  void ensureWorkspaceRecordsLoaded()

  await nextTick()
  focusSearchInput()
}

function closeSearch() {
  open.value = false
  searchQuery.value = ''
  highlightedIndex.value = 0
}

async function handleSelect(entry: SearchEntry) {
  closeSearch()

  if (entry.url) {
    rememberRecentUrl(entry.url)
    await navigateTo(entry.url)
    return
  }

  await entry.action?.()
}

function resolveShortcutLabel() {
  if (!import.meta.client) return

  shortcutLabel.value = /(Mac|iPhone|iPad)/i.test(window.navigator.platform)
    ? 'Cmd K'
    : 'Ctrl K'
}

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false

  return Boolean(
    target.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]'),
  )
}

function handleGlobalShortcut(event: KeyboardEvent) {
  if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return

  event.preventDefault()

  if (open.value) {
    inputRef.value?.focus()
    return
  }

  void openSearch()
}

function handleDocumentPointer(event: MouseEvent) {
  if (!open.value) return

  const target = event.target
  if (rootRef.value && target instanceof Node && !rootRef.value.contains(target)) {
    closeSearch()
  }
}

function handleInputKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    event.preventDefault()
    closeSearch()
    return
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    highlightedIndex.value = flattenedEntries.value.length === 0
      ? 0
      : Math.min(highlightedIndex.value + 1, flattenedEntries.value.length - 1)
    return
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0)
    return
  }

  if (event.key === 'Enter' && activeEntry.value) {
    event.preventDefault()
    void handleSelect(activeEntry.value)
  }
}

onMounted(() => {
  resolveShortcutLabel()
  loadRecentUrls()
  document.addEventListener('mousedown', handleDocumentPointer)
  window.addEventListener('keydown', handleGlobalShortcut)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleDocumentPointer)
  window.removeEventListener('keydown', handleGlobalShortcut)
})

watch(
  () => route.fullPath,
  () => {
    if (open.value) {
      closeSearch()
    }
  },
)

watch(open, async(value) => {
  if (!value) return

  highlightedIndex.value = 0
  await nextTick()
  focusSearchInput()
})

watch(searchQuery, () => {
  highlightedIndex.value = 0
})

watch(flattenedEntries, (entries) => {
  if (entries.length === 0) {
    highlightedIndex.value = 0
    return
  }

  if (highlightedIndex.value >= entries.length) {
    highlightedIndex.value = entries.length - 1
  }
})
</script>

<template>
  <div
    ref="rootRef"
    data-tour="workspace-command-menu"
    class="relative flex w-full items-center justify-center"
  >
    <button
      type="button"
      class="flex h-10 w-10 items-center justify-center rounded-full border border-border/70 bg-card/80 text-muted-foreground shadow-sm transition-all hover:bg-accent/70 hover:text-foreground md:hidden"
      @click="open ? closeSearch() : openSearch()"
    >
      <Search class="size-4" />
      <span class="sr-only">Open workspace search</span>
    </button>

    <div
      class="hidden transition-all duration-300 md:block"
      :class="open ? 'w-[min(42rem,58vw)]' : 'w-[min(26rem,40vw)]'"
    >
      <div
        class="flex h-11 items-center gap-3 rounded-full border border-border/70 px-3 shadow-sm backdrop-blur-xl transition-all duration-300"
        :class="open
          ? 'bg-card ring-2 ring-primary/14 shadow-[0_18px_48px_rgba(15,23,42,0.14)]'
          : 'bg-card/78 hover:bg-card/92 hover:shadow-md'"
        @click="openSearch"
      >
        <Search class="size-4 shrink-0 text-muted-foreground" />
        <input
          ref="inputRef"
          v-model="searchQuery"
          class="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
          placeholder="Search tasks, projects, clients, documents..."
          @focus="openSearch"
          @keydown="handleInputKeydown"
        >
        <span class="hidden shrink-0 rounded-full border border-border/70 px-2 py-0.5 text-[11px] text-muted-foreground lg:inline-flex">
          {{ shortcutLabel }}
        </span>
      </div>
    </div>

    <Transition
      enter-active-class="transition duration-180 ease-out"
      enter-from-class="-translate-y-2 opacity-0 scale-[0.98]"
      enter-to-class="translate-y-0 opacity-100 scale-100"
      leave-active-class="transition duration-140 ease-in"
      leave-from-class="translate-y-0 opacity-100 scale-100"
      leave-to-class="-translate-y-2 opacity-0 scale-[0.98]"
    >
      <div
        v-if="open"
        class="absolute left-1/2 top-[calc(100%+0.75rem)] z-[65] w-[min(46rem,calc(100vw-1rem))] -translate-x-1/2 overflow-hidden rounded-[1.6rem] border border-border/70 bg-popover shadow-[0_28px_80px_rgba(15,23,42,0.22)]"
      >
        <div class="border-b border-border/60 px-4 pb-3 pt-4 sm:px-5">
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/78">
                Workspace Search
              </p>
              <p class="mt-1 text-sm text-foreground">
                Search pages, project workspaces, task lists, and specific records in {{ workspaceName }}.
              </p>
            </div>
            <span class="rounded-full bg-foreground/6 px-2.5 py-1 text-[11px] text-muted-foreground">
              {{ shortcutLabel }}
            </span>
          </div>
          <p class="mt-2 text-xs text-muted-foreground">
            Try a project name, a task title, a client, or something like “project tasks”.
          </p>
          <div class="relative mt-3 md:hidden">
            <Search class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              ref="mobileInputRef"
              v-model="searchQuery"
              class="h-11 w-full rounded-xl border border-border/70 bg-background/82 pl-9 pr-3 text-sm text-foreground outline-none placeholder:text-muted-foreground"
              placeholder="Search anything..."
              @keydown="handleInputKeydown"
            >
          </div>
        </div>

        <div class="max-h-[min(72svh,40rem)] overflow-y-auto px-3 py-3 sm:px-4">
          <div
            v-if="workspaceRecordsLoading && searchQuery.trim().length > 0"
            class="mb-3 flex items-center gap-2 rounded-2xl border border-border/70 bg-background/70 px-3 py-2 text-xs text-muted-foreground"
          >
            <Loader2 class="size-3.5 animate-spin" />
            Loading workspace records...
          </div>

          <div
            v-for="section in filteredSections"
            :key="section.label"
            class="pb-4 last:pb-1"
          >
            <div class="flex items-center gap-2 px-2 pb-2">
              <component :is="section.icon" class="size-3.5 text-muted-foreground" />
              <p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/80">
                {{ section.label }}
              </p>
            </div>

            <div class="space-y-1">
              <button
                v-for="entry in section.entries"
                :key="entry.id"
                type="button"
                class="flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left transition-all hover:bg-muted/55"
                :class="entry === activeEntry ? 'bg-primary/9 text-foreground shadow-sm' : 'text-foreground/88'"
                @mouseenter="highlightedIndex = flattenedEntries.findIndex((candidate) => candidate.id === entry.id)"
                @click="handleSelect(entry)"
              >
                <span
                  class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border/70 bg-background/82"
                  :class="entry === activeEntry ? 'border-primary/24 bg-primary/10 text-primary' : 'text-muted-foreground'"
                >
                  <component :is="entry.icon" class="size-4" />
                </span>

                <span class="min-w-0 flex-1">
                  <span class="block truncate text-sm font-medium text-foreground">
                    {{ entry.title }}
                  </span>
                  <span class="block truncate text-xs text-muted-foreground">
                    {{ entry.description }}
                  </span>
                </span>

                <span
                  v-if="entry.isCurrent"
                  class="shrink-0 rounded-full bg-primary/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-primary"
                >
                  Current
                </span>
                <CornerDownLeft
                  v-else
                  class="size-4 shrink-0 text-muted-foreground/60"
                />
              </button>
            </div>
          </div>

          <div
            v-if="filteredSections.length === 0 || flattenedEntries.length === 0"
            class="rounded-[1.4rem] border border-dashed border-border/70 px-4 py-8 text-center"
          >
            <p class="text-sm font-medium text-foreground">Nothing matched that yet</p>
            <p class="mt-1 text-sm text-muted-foreground">
              Try a task name, client name, or the type of place you want to work in.
            </p>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>
